import { ExportResult, ExportResultCode } from '@opentelemetry/core';
import { OtelRemoteConfigs } from '@/telemetry-sdk';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ReadableSpan } from '@opentelemetry/sdk-trace-web';
import { SpanExporter } from '@opentelemetry/sdk-trace-base';
import { OTLPExporterNodeConfigBase } from '@opentelemetry/otlp-exporter-base';
import { diag } from '@opentelemetry/api';

const logger = diag.createComponentLogger({
  namespace: '✨ @telemetry-sdk/processors/dynamicTraceExporter',
});

export class DynamicTraceExporter implements SpanExporter {
  private exporter: OTLPTraceExporter | null;
  private enabled: boolean;
  private tracesUrl: string | undefined;
  private headers: any | undefined;

  constructor(tracesUrl: string, isEnabled: boolean, headers: any) {
    this.enabled = isEnabled;
    this.tracesUrl = tracesUrl;
    this.headers = headers;
    this.exporter = null;
    if (isEnabled) {
      this.exporter = this.createTraceExporter();
    }
  }

  updateConfigs(remoteConfigs: OtelRemoteConfigs): void {
    let traceConfigsChanged: boolean =
      remoteConfigs.tracingEnabled != this.enabled || remoteConfigs.tracesCollectorURL != this.tracesUrl;
    if (!traceConfigsChanged) {
      return;
    }

    logger.info(
      `Updating trace configs for DynamicTraceExporter enabled:${this.enabled} => ${remoteConfigs.tracingEnabled}, tracesUrl: ${this.tracesUrl} => ${remoteConfigs.tracesCollectorURL}`
    );

    this.enabled = remoteConfigs.tracingEnabled;
    this.tracesUrl = remoteConfigs.tracesCollectorURL;

    if (!this.enabled) {
      this.disable();
      return;
    }

    this.enable();
  }

  enable() {
    if (this.enabled) {
      return;
    }
    this.exporter = this.createTraceExporter();
    this.enabled = true;
  }

  private createTraceExporter() {
    const exporterOptions: OTLPExporterNodeConfigBase = {
      url: this.tracesUrl,
      headers: this.headers as Record<string, string> | undefined,
    };
    return new OTLPTraceExporter(exporterOptions);
  }

  disable() {
    this.exporter = null;
    this.enabled = false;
  }

  forceFlush(): Promise<void> {
    return this.exporter ? this.exporter.forceFlush() : Promise.resolve();
  }

  export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void {
    if (this.enabled && this.exporter) {
      this.exporter.export(spans, resultCallback);
    } else {
      // Drop spans if not enabled
      resultCallback({ code: ExportResultCode.SUCCESS });
    }
  }

  /** Stops the exporter. */
  shutdown(): Promise<void> {
    return this.exporter ? this.exporter.shutdown() : Promise.resolve();
  }
}
